import {
  recommendShopProductsArgsSchema,
  recommendShopProductsArtifactSchema,
  recommendShopProductsContentSchema,
} from '@kanbu/schema/contracts';
import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Sparkles } from 'lucide-react';
import { motion } from 'motion/react';

import { ShopProductCarousel } from './components/shop-product-carousel';
import { ToolError, ToolStatus } from '../components';
import { createToolComponent } from '../tool-registry';

/**
 * Renders the product cards the agent explicitly chose to recommend. The
 * artifact is hydrated server-side from product IDs — the model never
 * authors card data.
 */
export const RecommendShopProductsTool = createToolComponent(
  {
    schema: {
      args: recommendShopProductsArgsSchema,
      artifact: recommendShopProductsArtifactSchema,
      content: recommendShopProductsContentSchema,
    },
    validate: false,
  },
  ({ state, artifact, error }) => {
    // A product needs a name and URL to be displayed; a missing price is
    // shown as "Price not available" and 0 is a real (free) price.
    const products = artifact?.products?.filter(
      product => product.name && product.url,
    );

    switch (state) {
      case 'call':
      case 'call-partial':
      case 'call-complete':
        return (
          <ToolStatus loading>
            <Trans>Preparing recommendations...</Trans>
          </ToolStatus>
        );

      case 'error':
        return (
          <ToolError
            message={<Trans>Could not load recommendations</Trans>}
            error={error}
          />
        );

      case 'result':
        if (!products || products.length === 0) {
          return null;
        }

        return (
          <motion.div
            className='w-full'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <ToolStatus icon={<Sparkles />}>
              {plural(products.length, {
                one: 'Recommended product',
                other: 'Recommended # products',
              })}
            </ToolStatus>

            <ShopProductCarousel items={products} />
          </motion.div>
        );

      default:
        return null;
    }
  },
);
